/**
 * blackbody.ts — the colour of hot soot (fire realism R1, 2026-09-25).
 *
 * A wood fire's light is INCANDESCENT SOOT at roughly 1,000–1,500 K: yellow-
 * orange at the core, red where it cools, dark smoke above. A hand-picked LDR
 * gradient cannot hold that — the hot core never tone-maps to white and the
 * bloom is never driven on purpose. So the flame's colour is a TEMPERATURE,
 * looked up in a Planck LUT baked once at load (the Houdini Pyro Blackbody
 * approach):
 *
 *   Planck B(λ, T)  →  CIE 1931 2° XYZ (the standard observer's table,
 *   10 nm, interpolated)  →  linear Rec.709 / sRGB (D65),
 *   negative lobes clamped, NORMALISED so the largest channel is 1 (the
 *   CHROMA), with the RADIANCE carried separately as (T / T_max)⁴ — the
 *   Stefan–Boltzmann shape — so a shader scales it to its own exposure.
 *
 * Pure (no three at module scope): `verify:fire` checks the chromaticity
 * against the published Planckian locus and the radiance ratio against T⁴.
 */

/** The wood-fire soot range the LUT spans (K). */
export const FIRE_T_MIN = 800;
export const FIRE_T_MAX = 1600;
/** Texels in the LUT. */
export const BLACKBODY_LUT_SIZE = 256;

const H = 6.62607015e-34, C = 2.99792458e8, K = 1.380649e-23;

/** Planck spectral radiance B(λ, T) (λ in nm; arbitrary but consistent units). */
export function planck(lambdaNm: number, tK: number): number {
  const l = lambdaNm * 1e-9;
  const x = (H * C) / (l * K * tK);
  // expm1 keeps precision where x is small (not here, but it is the honest form)
  return (2 * H * C * C) / (l ** 5 * Math.expm1(x));
}

/**
 * CIE 1931 2° colour-matching functions, tabulated every 10 nm from 380 to
 * 780 nm (the standard observer). A multi-lobe Gaussian fit was tried first
 * and put the 1,000 K locus 0.011 off in x: at wood-fire temperatures almost
 * all the visible energy sits in the far red, exactly where the fit's x̄ tail
 * is half the real one — so the table, linearly interpolated.
 */
const CMF_380_780_10NM: ReadonlyArray<readonly [number, number, number]> = [
  [0.001368, 0.000039, 0.00645], [0.004243, 0.00012, 0.02005], [0.01431, 0.000396, 0.06785], [0.04351, 0.00121, 0.2074],
  [0.13438, 0.004, 0.6456], [0.2839, 0.0116, 1.3856], [0.34828, 0.023, 1.74706], [0.3362, 0.038, 1.77211],
  [0.2908, 0.06, 1.6692], [0.19536, 0.09098, 1.28764], [0.09564, 0.13902, 0.81295], [0.03201, 0.20802, 0.46518],
  [0.0049, 0.323, 0.272], [0.0093, 0.503, 0.1582], [0.06327, 0.71, 0.07825], [0.1655, 0.862, 0.04216],
  [0.2904, 0.954, 0.0203], [0.43345, 0.99495, 0.00875], [0.5945, 0.995, 0.0039], [0.7621, 0.952, 0.0021],
  [0.9163, 0.87, 0.00165], [1.0263, 0.757, 0.0011], [1.0622, 0.631, 0.0008], [1.0026, 0.503, 0.00034],
  [0.85445, 0.381, 0.00019], [0.6424, 0.265, 0.00005], [0.4479, 0.175, 0.00002], [0.2835, 0.107, 0],
  [0.1649, 0.061, 0], [0.0874, 0.032, 0], [0.04677, 0.017, 0], [0.0227, 0.00821, 0],
  [0.011359, 0.004102, 0], [0.00579, 0.002091, 0], [0.002899, 0.001047, 0], [0.00144, 0.00052, 0],
  [0.00069, 0.000249, 0], [0.000332, 0.00012, 0], [0.000166, 0.00006, 0], [0.000083, 0.00003, 0],
  [0.000042, 0.000015, 0],
];

/** CIE 1931 2° x̄ȳz̄ at λ (nm), linearly interpolated from the 10 nm table; 0 outside 380–780. */
export function cie1931(lambdaNm: number): [number, number, number] {
  const f = (lambdaNm - 380) / 10;
  if (f < 0 || f > CMF_380_780_10NM.length - 1) return [0, 0, 0];
  const i = Math.min(CMF_380_780_10NM.length - 2, Math.floor(f)), t = f - i;
  const a = CMF_380_780_10NM[i], b = CMF_380_780_10NM[i + 1];
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/** Integrate Planck × CMF over the visible band (380–780 nm, `step` nm). */
export function blackbodyXYZ(tK: number, step = 1): [number, number, number] {
  let X = 0, Y = 0, Z = 0;
  for (let l = 380; l <= 780; l += step) {
    const b = planck(l, tK);
    const [x, y, z] = cie1931(l);
    X += b * x; Y += b * y; Z += b * z;
  }
  return [X * step, Y * step, Z * step];
}

/** CIE xy chromaticity of a blackbody (the Planckian locus). */
export function blackbodyXY(tK: number): [number, number] {
  const [X, Y, Z] = blackbodyXYZ(tK);
  const s = X + Y + Z;
  return [X / s, Y / s];
}

/** XYZ → linear sRGB (Rec.709 primaries, D65). */
export function xyzToLinearSrgb(X: number, Y: number, Z: number): [number, number, number] {
  return [
    3.2404542 * X - 1.5371385 * Y - 0.4985314 * Z,
    -0.969266 * X + 1.8760108 * Y + 0.041556 * Z,
    0.0556434 * X - 0.2040259 * Y + 1.0572252 * Z,
  ];
}

/** The blackbody's CHROMA in linear sRGB: out-of-gamut lobes clamped, max channel = 1. */
export function blackbodyChroma(tK: number): [number, number, number] {
  const [X, Y, Z] = blackbodyXYZ(tK);
  const rgb = xyzToLinearSrgb(X, Y, Z).map((v) => Math.max(0, v)) as [number, number, number];
  const m = Math.max(rgb[0], rgb[1], rgb[2], 1e-12);
  return [rgb[0] / m, rgb[1] / m, rgb[2] / m];
}

/**
 * THE RENDERED FLAME (fire realism, 2026-10-08 — measured on 97 photographs, docs/research/2026-10-08-fire-realism).
 * Planck's chroma for 1,000–1,600 K soot is a deep red-orange (linear G/R 0.01–0.16, blue 0): the flames drew RED, and
 * by day a pink film. Unclipped daylight photographs of real flames read G/R 0.25–0.5. Two things move a real flame's
 * colour off the blackbody at its gas temperature: SOOT'S EMISSIVITY rises toward short wavelengths (ε ∝ λ^−1.39 for
 * luminous flames in the visible — Hottel & Broughton), and the viewer at a fire is ADAPTED toward its light. So the
 * LUT's chroma is the soot spectrum at `FIRE_COLOR_GAIN` × the gas temperature; its radiance stays the gas's own T⁴.
 * (1.2 and no more: under AgX — the default display transform — a yellower chroma only desaturates the flame to tan.)
 */
export const FIRE_SOOT_ALPHA = 1.39;
export const FIRE_COLOR_GAIN = 1.2;
/** The chroma a flame at gas temperature `tK` is DRAWN with (linear sRGB, max channel 1) — the LUT's rgb. */
export function flameChroma(tK: number, gain = FIRE_COLOR_GAIN, alpha = FIRE_SOOT_ALPHA): [number, number, number] {
  const tc = tK * gain;
  let X = 0, Y = 0, Z = 0;
  for (let l = 380; l <= 780; l += 2) {
    const b = planck(l, tc) * Math.pow(l / 560, -alpha);
    const [x, y, z] = cie1931(l);
    X += b * x; Y += b * y; Z += b * z;
  }
  const rgb = xyzToLinearSrgb(X, Y, Z).map((v) => Math.max(0, v)) as [number, number, number];
  const m = Math.max(rgb[0], rgb[1], rgb[2], 1e-12);
  return [rgb[0] / m, rgb[1] / m, rgb[2] / m];
}

/** Radiance relative to the LUT's top temperature — Stefan–Boltzmann's T⁴. */
export function blackbodyRadiance(tK: number, tMaxK = FIRE_T_MAX): number {
  return (tK / tMaxK) ** 4;
}

/** The temperature a LUT coordinate u ∈ [0, 1] stands for. */
export function lutTemperature(u: number, tMin = FIRE_T_MIN, tMax = FIRE_T_MAX): number {
  return tMin + (tMax - tMin) * Math.min(1, Math.max(0, u));
}

/**
 * The LUT itself: `size` RGBA texels, rgb = the flame's drawn chroma at T(u) (`flameChroma`), a = the
 * relative radiance (T/T_max)⁴. u = (i + 0.5) / size (texel centres, so a
 * linear-filtered fetch at u returns exactly this curve between centres).
 */
export function blackbodyLut(size = BLACKBODY_LUT_SIZE, tMin = FIRE_T_MIN, tMax = FIRE_T_MAX): Float32Array {
  const out = new Float32Array(size * 4);
  for (let i = 0; i < size; i++) {
    const t = tMin + ((i + 0.5) / size) * (tMax - tMin);
    const [r, g, b] = flameChroma(t); // THE RENDERED FLAME: the soot spectrum at the colour gain, not Planck at the gas temperature
    out[i * 4] = r; out[i * 4 + 1] = g; out[i * 4 + 2] = b; out[i * 4 + 3] = blackbodyRadiance(t, tMax);
  }
  return out;
}

/** Sample the LUT on the CPU the way a linear-filtered texture fetch does (the harness + the CPU oracle). */
export function sampleLut(lut: Float32Array, u: number): [number, number, number, number] {
  const n = lut.length / 4;
  const x = Math.min(n - 1, Math.max(0, u * n - 0.5));
  const i = Math.floor(x), j = Math.min(n - 1, i + 1), f = x - i;
  const o: [number, number, number, number] = [0, 0, 0, 0];
  for (let c = 0; c < 4; c++) o[c] = lut[i * 4 + c] * (1 - f) + lut[j * 4 + c] * f;
  return o;
}

/** A blackbody colour as a 0xRRGGBB hex for a light (linear chroma → the hex three reads as sRGB). */
export function blackbodyHex(tK: number): number { return chromaHex(blackbodyChroma(tK)); }
/** A FLAME's drawn colour at gas temperature `tK` as a hex (the sprite rung's gradient stops: the LUT's own colours). */
export function flameHex(tK: number): number { return chromaHex(flameChroma(tK)); }
function chromaHex([r, g, b]: [number, number, number]): number {
  const enc = (v: number): number => {
    const s = v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, Math.round(s * 255)));
  };
  return (enc(r) << 16) | (enc(g) << 8) | enc(b);
}
